import { useMutation } from '@apollo/client';
import { useRef } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Stack,
  useToast,
} from '@eluve/components';
import { LlmOutputTypesEnum } from '@eluve/graphql-types';

import {
  type LlmOutputType,
  type OutputTemplateOption,
  PromptTemplateDefaultForm,
  PromptTemplateDefaultFormValues,
} from './PromptTemplateDefaultForm';
import {
  PromptTemplateReference,
  insertPromptTemplateDefaultMutation,
  updatePromptTemplateDefaultMutation,
} from './promptTemplateDefaults.operations';

export interface PromptTemplateDefaultForEdit {
  id: string;
  tenantId: string | null;
  userId: string | null;
  llmOutputType: string;
  specialty: string | null;
  language: string | null;
  promptTemplateId: string;
  outputTemplateId: string | null;
  customInstructions: string | null;
}

interface PromptTemplateDefaultDialogProps {
  mode: 'add' | 'edit';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: 'global' | 'org' | 'user';
  tenantId?: string;
  userId?: string;
  llmOutputTypes: LlmOutputType[];
  promptTemplates: PromptTemplateReference[];
  outputTemplates?: OutputTemplateOption[];
  onRefreshTemplates?: () => void;
  // For edit mode
  defaultToEdit?: PromptTemplateDefaultForEdit;
  // For add mode - preselect output type
  preselectedOutputType?: string | null;
  // Output types that are disabled with reasons
  disabledOutputTypes?: Record<string, string>;
}

export const PromptTemplateDefaultDialog: React.FC<
  PromptTemplateDefaultDialogProps
> = ({
  mode,
  open,
  onOpenChange,
  entityType,
  tenantId,
  userId,
  llmOutputTypes,
  promptTemplates,
  outputTemplates,
  onRefreshTemplates,
  defaultToEdit,
  preselectedOutputType,
  disabledOutputTypes,
}) => {
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const isGlobal = entityType === 'global';
  const isOrg = entityType === 'org';
  const isEdit = mode === 'edit';

  const [insertDefault, { loading: insertLoading }] = useMutation(
    insertPromptTemplateDefaultMutation,
    {
      refetchQueries: isGlobal
        ? ['getGlobalPromptTemplateDefaults']
        : ['getTenantPromptTemplateDefaults'],
      update: (cache) => {
        // Increment the user's override count in cache
        if (entityType === 'user' && userId) {
          cache.modify({
            id: cache.identify({ __typename: 'Users', id: userId }),
            fields: {
              promptTemplateDefaultsAggregate(existing) {
                const currentCount = existing?.aggregate?.count ?? 0;
                return {
                  ...existing,
                  aggregate: {
                    ...existing?.aggregate,
                    count: currentCount + 1,
                  },
                };
              },
            },
          });
        }
      },
      onError: (error) => {
        if (error.message.includes('uniq_prompt_template_default_lookup')) {
          toast.error(
            'A default with this output type, specialty, and language already exists',
          );
        } else {
          toast.error(`Failed to create: ${error.message}`);
        }
      },
      onCompleted: () => {
        toast.success('Default created');
        onOpenChange(false);
      },
    },
  );

  const [updateDefault, { loading: updateLoading }] = useMutation(
    updatePromptTemplateDefaultMutation,
    {
      onError: (error) => {
        if (error.message.includes('uniq_prompt_template_default_lookup')) {
          toast.error(
            'A default with this output type, specialty, and language combination already exists. Delete this one instead.',
          );
        } else {
          toast.error(`Failed to update: ${error.message}`);
        }
      },
      onCompleted: () => {
        toast.success('Default updated');
        onOpenChange(false);
      },
    },
  );

  const loading = insertLoading || updateLoading;

  const handleSubmit = async (values: PromptTemplateDefaultFormValues) => {
    if (isEdit && defaultToEdit) {
      await updateDefault({
        variables: {
          id: defaultToEdit.id,
          tenantId: defaultToEdit.tenantId,
          userId: defaultToEdit.userId,
          llmOutputType: defaultToEdit.llmOutputType as LlmOutputTypesEnum,
          specialty: values.specialty,
          language: values.language,
          promptTemplateId: values.promptTemplateId,
          outputTemplateId: values.outputTemplateId,
          customInstructions: values.customInstructions,
        },
      });
    } else {
      // For global: both tenantId and userId are null
      // For org: tenantId is set, userId is null
      // For user: both tenantId and userId are set
      const effectiveTenantId = isGlobal ? null : tenantId;
      const effectiveUserId = isGlobal || isOrg ? null : userId;

      await insertDefault({
        variables: {
          tenantId: effectiveTenantId,
          userId: effectiveUserId,
          llmOutputType: values.llmOutputType as LlmOutputTypesEnum,
          specialty: values.specialty,
          language: values.language,
          promptTemplateId: values.promptTemplateId,
          outputTemplateId: values.outputTemplateId,
          customInstructions: values.customInstructions,
        },
      });
    }
  };

  const handleSaveClick = () => {
    formRef.current?.requestSubmit();
  };

  const title = isEdit
    ? isGlobal || isOrg
      ? 'Edit Default'
      : 'Edit Override'
    : isGlobal || isOrg
      ? 'Add Default'
      : 'Add Override';

  // Prepare default values for edit mode or add mode with preselected output type
  const defaultValues: Partial<PromptTemplateDefaultFormValues> | undefined =
    isEdit && defaultToEdit
      ? {
          scope: isGlobal ? 'global' : isOrg ? 'tenant' : 'user',
          tenantId: defaultToEdit.tenantId,
          userId: defaultToEdit.userId,
          llmOutputType: defaultToEdit.llmOutputType,
          specialty: defaultToEdit.specialty,
          language: defaultToEdit.language,
          promptTemplateId: defaultToEdit.promptTemplateId,
          outputTemplateId: defaultToEdit.outputTemplateId,
          customInstructions: defaultToEdit.customInstructions,
        }
      : preselectedOutputType
        ? { llmOutputType: preselectedOutputType }
        : undefined;

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <PromptTemplateDefaultForm
          key={defaultToEdit?.id ?? 'add'}
          formRef={formRef}
          hideSubmitButton
          fixedScope={isGlobal ? 'global' : isOrg ? 'tenant' : 'user'}
          fixedTenantId={tenantId}
          fixedUserId={isGlobal || isOrg ? undefined : userId}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          isLoading={loading}
          llmOutputTypes={llmOutputTypes}
          promptTemplates={promptTemplates}
          outputTemplates={outputTemplates}
          onRefreshTemplates={onRefreshTemplates}
          disableOutputType={isEdit}
          disabledOutputTypes={disabledOutputTypes}
          hideSpecialty={!isGlobal && !isOrg}
        />
        <DialogFooter>
          <Stack gap={2} justify="end" direction="horizontal">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={loading} onClick={handleSaveClick}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
